import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Add as AddIcon, Visibility } from '@mui/icons-material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { PurchaseOrder, PurchaseOrderStatus } from '../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';

// PurchaseOrderController.create's @PreAuthorize: StoreAdmin, InstitutionAdmin. InstitutionManager can
// list and approve but would get a 403 creating, so it is not offered the button.
const CREATE_ROLES = ['StoreAdmin', 'InstitutionAdmin'];

type FilterOption = PurchaseOrderStatus | undefined;

interface StatusChip {
  label: string;
  value: FilterOption;
}

const STATUS_CHIPS: StatusChip[] = [
  { label: 'All', value: undefined },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Pending Approval', value: 'PENDING_APPROVAL' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Ordered', value: 'ORDERED' },
  { label: 'Partially Received', value: 'PARTIALLY_RECEIVED' },
  { label: 'Received', value: 'RECEIVED' },
  { label: 'Closed', value: 'CLOSED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const STATUS_COLORS: Record<PurchaseOrderStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  ORDERED: 'primary',
  PARTIALLY_RECEIVED: 'secondary',
  RECEIVED: 'success',
  CLOSED: 'default',
  REJECTED: 'error',
  CANCELLED: 'error',
};

export const PurchaseOrderList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState<FilterOption>(undefined);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const fetchPOs = async (page: number = 0, filter: FilterOption = statusFilter) => {
    try {
      setLoading(true);
      const response = await PurchaseOrderService.listByInstitution(page, 10, filter);
      setPos(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs(0, statusFilter);
  }, [statusFilter]);

  const handleChipClick = (value: FilterOption) => {
    setStatusFilter(value);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchPOs(page - 1, statusFilter);
  };

  const rawRoles = AuthService.getCurrentUser()?.roles;
  const userRoles: string[] = Array.isArray(rawRoles) ? rawRoles : rawRoles ? [rawRoles] : [];
  const canCreate = userRoles.some(r => CREATE_ROLES.includes(r));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Purchase Orders</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/purchase-orders/new')}>
            New Purchase Order
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {STATUS_CHIPS.map(chip => (
          <Chip
            key={chip.label}
            label={chip.label}
            clickable
            variant={statusFilter === chip.value ? 'filled' : 'outlined'}
            color={statusFilter === chip.value ? 'primary' : 'default'}
            onClick={() => handleChipClick(chip.value)}
          />
        ))}
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>PO Number</TableCell>
                <TableCell>Supplier ID</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pos.map(po => (
                <TableRow key={po.id}>
                  <TableCell>{po.poNumber}</TableCell>
                  <TableCell>{po.supplierId}</TableCell>
                  <TableCell>{po.orderDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={po.status}
                      color={STATUS_COLORS[po.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{po.totalAmount}</TableCell>
                  <TableCell>
                    <Tooltip title="View" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/procurement/purchase-orders/${po.id}`)}
                        aria-label="View"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageInfo.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageInfo.totalPages}
            page={pageInfo.currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default PurchaseOrderList;
